import React from 'react';
import { i18n } from '@international/mastodon-i18n';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import IdDocumentVerification from 'components/Verification/IdDocumentVerification/IdDocumentVerification';
import Auth from 'containers/Auth';
import SsnCheckVerification from 'components/Verification/SsnCheckVerification/SsnCheckVerification';
import MainContainer from '../../components/MainContainer';
import Loading from '../../components/Loading';
import ResourceNotFound from '../ResourceNotFound';
import { verificationAccessScreen } from '../../components/Verification/VerificationAccessScreen';
import classify from '../../lib/classify';
import CandidateEmploymentHistoryVerification from '../../components/Verification/CandidateEmploymentHistoryVerification';
import EducationVerification from '../../components/Verification/EducationVerification';
import IdVerification from '../../components/Verification/IdVerification';
import PhotoVerification from '../../components/Verification/PhotoVerification';
import DriverLicenseVerification from '../../components/Verification/DriverLicenseVerification';
import SsnCheck from '../../components/Verification/SsnCheck';
import MatrixVerification from '../../components/Verification/MatrixVerification';
import useVerification from '../../api/verifications/useVerification';
import {
  VERIFICATION_SUBMIT_MUTATION_KEY,
  normalizeVerificationId,
} from '../../api/verifications/verificationCache';
import {
  submitEducationVerification as submitEducationVerificationAction,
  submitEmploymentVerification as submitEmploymentVerificationAction,
  submitMatrixVerification as submitMatrixVerificationAction,
  submitSSNVerificationWithoutDocs as submitSSNVerificationWithoutDocsAction,
  submitVerificationDefault as submitVerificationDefaultAction,
} from '../../actions';
import useBffHardening from '../../api/harness/useBffHardening';
import useVerificationSession from '../../utils/useVerificationSession';
import { useAuthStatus } from '../../utils/useAuthReady';
import useVerificationAccessRetry from '../../utils/useVerificationAccessRetry';
import './Status.scss';

// Through `connect`, each submit prop dispatches an async thunk from
// actions/verifications, so it resolves when the submit succeeds and rejects
// when it fails.
type SubmitFn = (data: any) => Promise<void>;

export interface StatusProps {
  submitEducationVerification: SubmitFn;
  submitEmploymentVerification: SubmitFn;
  submitMatrixVerification: SubmitFn;
  submitVerificationDefault: SubmitFn;
  submitSSNVerificationWithoutDocs: SubmitFn;
}

const ComponentMapping: Record<string, any> = {
  CandidateEmploymentHistoryVerification,
  EducationVerification,
  PhotoVerification,
  DriverLicenseVerification,
  DriverLicenseHistoryVerification: DriverLicenseVerification,
  SsnCheck,
  SsnCheckFailure: SsnCheck,
  IdDocumentVerification: IdVerification,
  IdVerification,
  LicenseDocumentVerification: IdVerification,
  PassportDocumentVerification: IdVerification,
  PreviousLicenseDocumentVerification: IdVerification,
  SsnDocumentVerification: IdVerification,
  MatrixVerification,
};

const VerificationComponentMapping: Record<string, any> = {
  ...ComponentMapping,
  SsnCheck: SsnCheckVerification,
};

const IdVerificationComponentMapping: Record<string, any> = {
  ...VerificationComponentMapping,
  IdVerification: IdDocumentVerification,
};

const getVerificationComponent = (verification: Record<string, any>) => {
  return IdVerificationComponentMapping[classify(verification.object)];
};

export const Status: React.FC<StatusProps> = ({
  submitEducationVerification,
  submitEmploymentVerification,
  submitSSNVerificationWithoutDocs,
  submitVerificationDefault,
  submitMatrixVerification,
}) => {
  const params = useParams();
  const verificationId = normalizeVerificationId(params?.verificationId);
  const {
    verification,
    fetching,
    errors,
    access,
    fetch: fetchVerification,
    syncFromStore,
  } = useVerification(verificationId);

  React.useEffect(() => {
    const pageTitle = i18n.getStr('containers.page.title');
    const verificationWords = verification
      ? verification.object.split('_')
      : [];
    const verificationStart = verificationWords.length
      ? verificationWords[0].charAt(0).toUpperCase() +
        verificationWords[0].slice(1).toLowerCase()
      : '';
    const verificationEnd = verificationWords.slice(1).join(' ');

    document.title = verificationStart
      ? `${pageTitle} - ${verificationStart} ${verificationEnd}`
      : pageTitle;
  }, [verification]);
  const sessionReady = useVerificationSession(verificationId);
  const { enabled: sessionsOn } = useBffHardening();
  const { loading: authLoading, isLoggedIn } = useAuthStatus();
  const loaded = verification?.id === verificationId;
  const { waitingToRetry, retryNow, markRetried } = useVerificationAccessRetry({
    access,
    errors,
    authLoading,
    verificationId,
  });

  React.useEffect(() => {
    if (!sessionReady || fetching || loaded || (errors && !retryNow)) return;
    if (errors) markRetried();
    fetchVerification();
  }, [
    sessionReady,
    fetching,
    loaded,
    errors,
    retryNow,
    markRetried,
    fetchVerification,
    verificationId,
  ]);

  // A session that becomes ready after the verification already loaded (a
  // stale cached flag read off at first), or while a fetch started without a
  // session is still in flight, needs the fetch repeated with it. The pending
  // flag remembers the change across renders until the in-flight fetch settles.
  const sessionWasReady = React.useRef(sessionReady);
  const refetchPending = React.useRef(false);
  React.useEffect(() => {
    if (sessionReady && !sessionWasReady.current) {
      refetchPending.current = true;
    }
    sessionWasReady.current = sessionReady;
    if (refetchPending.current && !fetching) {
      refetchPending.current = false;
      // When nothing has loaded or errored, or a leftover access failure is
      // due its retry, the effect above does the fetch in this same commit.
      if (loaded || (errors && !retryNow)) {
        fetchVerification();
      }
    }
  }, [
    sessionReady,
    loaded,
    errors,
    retryNow,
    fetching,
    fetchVerification,
    verificationId,
  ]);

  const submitVerification = (verificationType: string): SubmitFn => {
    const customSubmitFunctions: Record<string, SubmitFn> = {
      education_verification: submitEducationVerification,
      candidate_employment_history_verification: submitEmploymentVerification,
      ssn_check: submitSSNVerificationWithoutDocs,
      matrix_verification: submitMatrixVerification,
    };

    return customSubmitFunctions[verificationType] ?? submitVerificationDefault;
  };

  // The submit runs as a mutation so a failure (no response, an error status,
  // or a rejected file size) ends in an error state on this screen instead of
  // in the Redux `errors` that replaces the form with "not found". The thunks
  // still dispatch the success action, so the readers of state.verifications
  // that have not moved (analytics, the OCR hooks) keep working, and on success
  // the new verification is copied into the query data the page reads. The
  // mutation key is how the provider knows a submit is running. The error
  // clears when the next submit starts.
  const submitMutation = useMutation({
    mutationKey: VERIFICATION_SUBMIT_MUTATION_KEY,
    mutationFn: (data: any) => submitVerification(verification?.object)(data),
    onSuccess: syncFromStore,
  });
  // TanStack Query keeps `mutate` stable, so the handler keeps its identity
  // between renders.
  const { mutate: submit } = submitMutation;
  const handleSubmit = React.useCallback((data: any) => submit(data), [submit]);

  // Keep loading while the verification session is still being prepared
  // (including while the Harness flag resolves), so the page does not fall
  // through to ResourceNotFound before any fetch has started. With BFF
  // hardening on, also keep loading until this verification has loaded or the
  // fetch errored, so the render between sessionReady flipping and the fetch
  // starting does not flash ResourceNotFound.
  if (
    fetching ||
    (verificationId && !sessionReady) ||
    (sessionsOn && verificationId && !loaded && !errors) ||
    waitingToRetry ||
    retryNow
  ) {
    return (
      <MainContainer data-testid='verification-page'>
        <Loading />
      </MainContainer>
    );
  }

  const accessScreen =
    !loaded && verificationAccessScreen({ access, verificationId, isLoggedIn });
  if (accessScreen) {
    return (
      <MainContainer data-testid='verification-page'>
        {accessScreen}
      </MainContainer>
    );
  }

  if (!verification || errors) {
    return (
      <MainContainer data-testid='verification-page'>
        <ResourceNotFound />
      </MainContainer>
    );
  }
  const VerificationComponent = getVerificationComponent(verification);
  return (
    <Auth isVerification>
      <VerificationComponent
        onSubmit={handleSubmit}
        verificationError={submitMutation.error}
      />
    </Auth>
  );
};

const actions = {
  submitEducationVerification: submitEducationVerificationAction,
  submitEmploymentVerification: submitEmploymentVerificationAction,
  submitSSNVerificationWithoutDocs: submitSSNVerificationWithoutDocsAction,
  submitVerificationDefault: submitVerificationDefaultAction,
  submitMatrixVerification: submitMatrixVerificationAction,
};

export default connect(null, actions)(Status);
